/**
 * **C12 · The family fulfillment statement** (Rebuild 59 `Documents.dc.html` #c12; F17, ADR 0104):
 * one letter page a school family, after the festival — the commitment, what we have for the
 * family credit by credit with its source and who recorded it, the total, the balance, the
 * reply-by sentence and the Festival Director's line from Roles. It lays out Family fulfillment's
 * own read for the one family and computes nothing: every figure is the one engine's
 * (`units_earned_per_family`) — the total is the row's earned quarters, never a sum of its lines,
 * and the balance is the engine's own sentence and what the row says is missing, never
 * `standing()` asked again, which without the ruling would contradict a year ruled full.
 *
 * Credits where the drawing writes dollars (canvas defect 334). Where the year was disputed, the
 * ruling sits in the balance's middle cell, which the drawing leaves empty (canvas defect 336): the
 * engine's words for it, who ruled as the file writes it and the day — never the finding or the
 * claim, which only Family fulfillment shows. A field nobody recorded reads "no data"; what the
 * read did not hand over is NO DATA, never a sample.
 */
import { festivalDayWords } from './count_papers.js';
import { NO_DATA, type PaperPage } from './paper_page.js';
import { letterDay } from './payee_letters.js';
import { QUARTERS, rulingSays, saysCredits, type CreditWord } from './units_earned_per_family.js';
import { longDay, shortDay } from './year_end_page.js';

/** The school, as the shortfall letter names it (print/shortfall-letters.tsx), as drawn. */
const SCHOOL = 'Saint Lawrence Catholic School';
/** A field nobody recorded, in Family fulfillment's words (Rebuild 59 #ful02's rule). */
const UNRECORDED = 'no data';
/** The reply-by day while Year settings holds no date for the shortfall letters. */
const NOT_SET = 'NOT SET';

/**
 * The most credit lines the letter page holds a row each, with the total, the balance, the reply
 * and the signature still on it: the sheet is a fixed 792 px that cuts off what runs past its foot,
 * and a credit's three cells wrap to three lines at the most. A family with more prints a row a
 * kind instead — how many entries, and the kind's credits as the read serves them — so nothing
 * below the lines is ever lost (canvas defect 336).
 */
export const STATEMENT_LINES = 6;

/** A credit's line as Family fulfillment hands it on, the shift's hours already in words. */
export interface StatementLine {
  quarters: number;
  /** Whose hand earned it, by name; a sponsorship's has none. */
  who: string | null;
  /** Where: the area the hours went to, the area chaired, a sponsorship's organization · level. */
  what: string | null;
  /** The shift's operating day, YYYY-MM-DD. */
  day: string | null;
  /** The shift's hours as the print's one formatter writes them: "12 PM – 4 PM". */
  hours: string | null;
  /** Who checked them in. */
  inBy: string | null;
  /** The area a move sent them to. */
  movedTo: string | null;
  /** Who recorded the credit — who confirmed a chair's shift, who linked a sponsorship. */
  recordedBy: string | null;
  /** The day it was recorded, YYYY-MM-DD. */
  recordedOn: string | null;
  /** The credit's own words, as written. */
  note: string | null;
}

/** A family's credits of one kind, in Family fulfillment's order. */
export interface StatementGroup {
  kind: 'shift' | 'sponsorship' | 'attested' | 'chair_role' | 'standby';
  /** The kind as Family fulfillment names it: Shifts, Sponsorship, Chair service, Chair, Standby. */
  label: string;
  /** The kind's quarters as the read serves them: what a folded row prints, never added up here. */
  quarters: number;
  lines: StatementLine[];
}

/** A disputed year's ruling as the statement prints it: the dispute, its word, who, the day. */
export interface StatementRuling {
  disputeNo: string;
  /** Null while the dispute is not ruled yet — never read as none. */
  creditResult: CreditWord | null;
  /** As the file writes it. */
  ruledBy: string | null;
  /** YYYY-MM-DD. */
  ruledOn: string | null;
}

/** The family as Family fulfillment's read hands it on — never its finding or its claim. */
export interface StatementFamily {
  name: string;
  /** The students as the school lists them. */
  students: { name: string; grade: string | null }[];
  status: 'Complete' | 'Partial' | 'At risk' | 'Exempt';
  /** Quarters earned: the one engine's figure, which the total prints. */
  quarters: number;
  /** Quarters that fulfill the year. */
  toFulfill: number;
  /** The engine's sentence for the year: "1¼ of 2 — 3 more hours to go". */
  says: string;
  /** What the year still asks; null once it is complete or excused. */
  missing: string | null;
  ruling: StatementRuling | null;
  sources: StatementGroup[];
}

export interface FulfillmentPageInput {
  /** The festival year the statement is for. */
  year: number;
  /**
   * Family fulfillment's answer for the one family: the family, the day to reply by (the day
   * before the shortfall letters go; null while Year settings holds no date) and where to write.
   * Null when the read did not answer.
   */
  read: { family: StatementFamily | null; replyBy: string | null; writeTo: string } | null;
  /** The day it is printed, YYYY-MM-DD (Detroit). */
  day: string;
  /** The one Festival Director Roles names; null when it names none or several. */
  director: string | null;
}

/** "2 credits", "1 credit", "¾ credit" — quarters in the engine's own words. */
const credits = (q: number): string =>
  `${saysCredits(q)} ${q > 0 && q <= QUARTERS ? 'credit' : 'credits'}`;

/** The family's own by first name: "Russet Winesap" on the Winesaps' statement is Russet. */
function own(name: string | null, family: string): string {
  if (name === null) return UNRECORDED;
  const tail = ` ${family}`.toLowerCase();
  return name.length > tail.length && name.toLowerCase().endsWith(tail)
    ? name.slice(0, name.length - tail.length)
    : name;
}

/** A shift's day as the drawing writes it, "Sat Sep 12", and its hours after it. */
function when(day: string | null, hours: string | null = null): string {
  if (day === null) return UNRECORDED;
  const words = festivalDayWords(day, true);
  return hours === null ? words : `${words}, ${hours}`;
}
const on = (ymd: string | null): string => (ymd === null ? UNRECORDED : shortDay(ymd));

/**
 * A move, as the read names it — the area it sent them to: "moved here" on the hours worked there
 * (a booth's reassignment checks them in with its words), "moved to <area>" on the hours before.
 */
function moved(l: StatementLine): string {
  if (!l.movedTo) return '';
  return l.movedTo === l.what ? ' (moved here)' : ` (moved to ${l.movedTo})`;
}

type Words = (l: StatementLine, family: string) => [string, string];

/**
 * Each kind's line as the drawing writes its four: what was given — who, where, when — and its
 * source with who recorded it. Where these words are not C12's, and the kinds and states C12 does
 * not draw (a chair's year, standby, no credit line, an excused year), canvas defect 336 says so.
 */
const LINE: Record<StatementGroup['kind'], Words> = {
  shift: (l, f) => [
    [own(l.who, f), l.what ?? UNRECORDED, when(l.day, l.hours)].join(' · ') + moved(l),
    `check-in · ${l.inBy ?? UNRECORDED}`,
  ],
  sponsorship: (l) => [
    l.what === null ? `Sponsorship · ${UNRECORDED}` : `${l.what} sponsorship`,
    `linked ${on(l.recordedOn)} · ${l.recordedBy ?? UNRECORDED}`,
  ],
  attested: (l, f) => [
    [own(l.who, f), l.what ?? UNRECORDED, when(l.day)].join(' · '),
    `chair-confirmed · ${l.recordedBy ?? UNRECORDED}`,
  ],
  chair_role: (l, f) => [[own(l.who, f), l.what ?? UNRECORDED].join(' · '), 'the chair’s year'],
  standby: (l, f) => [
    [own(l.who, f), l.note ?? 'standby', on(l.recordedOn)].join(' · '),
    `standby · ${l.recordedBy ?? UNRECORDED}`,
  ],
};

/**
 * A kind's credits as one row, once a family has more lines than the page holds: the kind, how many
 * entries the record holds, and the kind's credits as the read serves them — never a sum of lines.
 */
const folded = (g: StatementGroup): string[] => [
  `${g.label} · ${String(g.lines.length)} ${g.lines.length === 1 ? 'entry' : 'entries'}`,
  'each one on request',
  credits(g.quarters),
];

/** What the year still asks, in the engine's own words: complete, excused, or the hours left. */
function balance(f: StatementFamily): string {
  if (f.status === 'Exempt') return 'excused';
  if (f.missing === null) return 'complete';
  // The engine's sentence carries the hours ("— 3 more hours to go"); one it cannot read is NO
  // DATA, never a guess.
  return / — (\d+(?:\.\d+)? more hours?) to go/.exec(f.says)?.[1] ?? NO_DATA;
}

/** The ruling where the year was disputed: the engine's words, who ruled as written, the day. */
function ruled(r: StatementRuling): string {
  if (r.creditResult === null) return rulingSays(r);
  const day = r.ruledOn === null ? UNRECORDED : letterDay(r.ruledOn);
  return `${rulingSays(r)} · by ${r.ruledBy ?? UNRECORDED} · ${day}`;
}

export function fulfillmentPage(i: FulfillmentPageInput): PaperPage {
  const year = String(i.year);
  const family = i.read?.family ?? null;
  const students = family
    ? family.students.map((s) => {
        const name = own(s.name, family.name);
        return s.grade ? `${name} (grade ${s.grade})` : name;
      })
    : [];
  // A row a credit while the page holds them; past that, a row a kind (STATEMENT_LINES).
  const fold = family !== null && family.sources.flatMap((g) => g.lines).length > STATEMENT_LINES;
  const given = family
    ? fold
      ? family.sources.map(folded)
      : family.sources.flatMap((g) =>
          g.lines.map((l) => [...LINE[g.kind](l, family.name), credits(l.quarters)]),
        )
    : [[NO_DATA, '', '']];
  const total = family ? credits(family.quarters) : NO_DATA;
  const left = family ? balance(family) : NO_DATA;
  const ruling = family?.ruling ? ruled(family.ruling) : '';
  const replyBy =
    i.read === null ? NO_DATA : i.read.replyBy === null ? NOT_SET : longDay(i.read.replyBy);
  const writeTo = i.read?.writeTo ?? NO_DATA;
  // A reply-by day already past on the day it is printed is never printed: the page says where to
  // write, without a day, until James says which day a late statement carries.
  const past = i.read !== null && i.read.replyBy !== null && i.read.replyBy < i.day;
  const reply = past
    ? `If this is not what you did, write to ${writeTo}.`
    : `If this is not what you did, reply by ${replyBy} — write to ${writeTo}.`;
  return {
    title: `Your ${year} Apple Fest commitment`,
    sub: [
      family ? `The ${family.name} family` : NO_DATA,
      ...students,
      SCHOOL,
      letterDay(i.day),
    ].join(' · '),
    blocks: [
      {
        head: 'THE COMMITMENT',
        cols: '1fr 110px',
        rows: [[`Each school family · ${year}`, family ? credits(family.toFulfill) : NO_DATA]],
      },
      {
        head: 'WHAT WE HAVE FOR YOU',
        cols: '1fr 1fr 90px',
        rows: [
          ...(given.length ? given : [['Nothing recorded for this year', '', '']]),
          ['TOTAL CREDIT', '', total],
          ['BALANCE', ruling, left],
        ],
      },
      { head: '', cols: '1fr', rows: [[reply]] },
    ],
    sign: [`${i.director ?? ''} · Festival Director`, ''],
    foot: 'Fulfillment statement · one per family · from People & roles → Family fulfillment',
  };
}
